import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Col, Card, Navbar, Nav, Modal, Button, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import {
  User,
  authService,
  friendshipService,
  inventoryService,
  loadUnreadNotificationCount,
  type InventoryItemDto,
  type InventoryPageDto,
} from '../services/api';
import LanguageSelector from './LanguageSelector';
import NavbarBrandLogo from './NavbarBrandLogo';
import UserNavbarMenus from './UserNavbarMenus';
import NotificationBell from './NotificationBell';
import NavbarMusicToggle from './NavbarMusicToggle';
import PwaInstallButton from './PwaInstallButton';
import LoginPage from './LoginPage';
import FriendsList from './FriendsList';
import PendingRequests from './PendingRequests';
import InventoryPotionSprite, { inventoryPotionGridMeta } from './InventoryPotionSprite';
import PulseBadge from './shared/PulseBadge';
import './HomePage.css';
import './UserProfile.css';
import './InventoryPage.css';

function itemIconClass(_itemTypeKey: string): string {
  return 'bi-box-seam text-secondary';
}

const experiencePotionTypes = new Set(['SmallExperiencePotion', 'MediumExperiencePotion', 'LargeExperiencePotion']);

function isExperiencePotionItem(itemTypeKey: string): boolean {
  return experiencePotionTypes.has(itemTypeKey);
}

function xpPotionBonusPercentForItem(itemTypeKey: string): number | null {
  switch (itemTypeKey) {
    case 'SmallExperiencePotion':
      return 30;
    case 'MediumExperiencePotion':
      return 70;
    case 'LargeExperiencePotion':
      return 100;
    default:
      return null;
  }
}

/** Matches API life units granted per potion (`InventoryService` health items). */
const MAX_LIFE_UNITS_DEFAULT = 10;

const healthPotionTypes = new Set(['SmallHealthPotion', 'MediumHealthPotion', 'LargeHealthPotion']);

function isHealthPotionItem(itemTypeKey: string): boolean {
  return healthPotionTypes.has(itemTypeKey);
}

function healthPotionLifeRestoreCount(itemTypeKey: string): number {
  switch (itemTypeKey) {
    case 'SmallHealthPotion':
      return 1;
    case 'MediumHealthPotion':
      return 2;
    case 'LargeHealthPotion':
      return 6;
    default:
      return 0;
  }
}

/** Preview gain after server clamp (same math as `GrantLifeUnitsAsync`). */
function healthPotionEffectiveRestore(current: number, maxLives: number, restore: number): { effective: number; wasted: number } {
  const room = Math.max(0, maxLives - current);
  const effective = Math.min(restore, room);
  return { effective, wasted: restore - effective };
}

function shouldWarnHealthPotionOverflow(user: User | null, restore: number): boolean {
  if (!user || restore <= 0) return false;
  const current = user.livesStatus?.lives ?? user.lives ?? 0;
  const max = user.livesStatus?.maxLives ?? MAX_LIFE_UNITS_DEFAULT;
  return current + restore > max;
}

/**
 * Converts stored life units (0–10, 2 units = 1 heart) to ubiquitous heart phrasing for UI copy.
 * Matches item descriptions ("meio coração", "1 coração", …).
 */
function lifeUnitsAsHeartPhrase(
  units: number,
  translate: (key: string, options?: Record<string, unknown>) => string,
): string {
  const u = Math.max(0, Math.min(10, Math.round(units)));
  return translate(`inventory.lifeUnitsAsHearts.${u}`);
}

/** Composes health partial warning using heart fractions (same vocabulary as inventory item texts). */
function buildHealthPotionPartialMessage(
  translate: (key: string, options?: Record<string, unknown>) => string,
  current: number,
  max: number,
  restore: number,
  effective: number,
  wasted: number,
): string {
  const hr = (n: number) => lifeUnitsAsHeartPhrase(n, translate);
  /** Barra cheia / nada entra: mensagem única pedida para UX (poção pequena = meio coração, etc.). */
  if (effective === 0) {
    return translate('inventory.healthPotionPartialNoEffect', { hearts: hr(restore) });
  }
  const restorePart = translate('inventory.healthPotionPartialRestoreIntro', { hearts: hr(restore) });
  const currentHearts = hr(current);
  const maxHearts = hr(max);
  const effectivePart = translate('inventory.healthPotionPartialEffectiveRecover', { hearts: hr(effective) });
  const wastedPart = translate('inventory.healthPotionPartialWasted', { hearts: hr(wasted) });
  return translate('inventory.healthPotionPartialCompose', {
    restorePart,
    currentHearts,
    maxHearts,
    effectivePart,
    wastedPart,
  });
}

/** ISO from API without offset is treated as UTC (datetime2). */
function parseUtcIsoMs(iso: string): number {
  const s = iso.trim();
  const hasZone = /Z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  return Date.parse(hasZone ? s : `${s}Z`);
}

/** Prefer server `minutesRemaining`; fallback uses UTC parse (not local-midnight). */
function xpBuffMinutesRemainingForDisplay(b: { minutesRemaining?: number; expiresAtUtc: string }): number {
  if (typeof b.minutesRemaining === 'number' && Number.isFinite(b.minutesRemaining)) {
    return Math.max(0, b.minutesRemaining);
  }
  const msLeft = Math.max(0, parseUtcIsoMs(b.expiresAtUtc) - Date.now());
  return msLeft <= 0 ? 0 : Math.max(1, Math.floor(msLeft / 60_000));
}

const InventoryPage: React.FC = () => {
  const { t, ready, i18n } = useSafeTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showPendingRequests, setShowPendingRequests] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const [inventory, setInventory] = useState<InventoryPageDto | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [detailItem, setDetailItem] = useState<InventoryItemDto | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmXpReplace, setConfirmXpReplace] = useState(false);
  const [confirmHealthPartial, setConfirmHealthPartial] = useState(false);
  const [using, setUsing] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const loadUser = useCallback(async () => {
    setIsLoadingUser(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUser(null);
        setUnreadNotificationCount(0);
        return;
      }
      const u = await authService.getProfile();
      setUser(u);
      const unread = await loadUnreadNotificationCount(u);
      setUnreadNotificationCount(unread);
      try {
        const pending = await friendshipService.getPendingRequests();
        setPendingRequestsCount(pending.length);
      } catch {
        setPendingRequestsCount(0);
      }
    } catch {
      setUser(null);
      setUnreadNotificationCount(0);
    } finally {
      setIsLoadingUser(false);
    }
  }, []);

  const loadInventory = useCallback(async () => {
    if (!localStorage.getItem('authToken')) {
      setInventory(null);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    setLoadError(false);
    try {
      const data = await inventoryService.getMine();
      setInventory(data);
    } catch {
      setLoadError(true);
      setInventory(null);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (!isLoadingUser && user) {
      void loadInventory();
    } else if (!isLoadingUser && !user) {
      setListLoading(false);
    }
  }, [isLoadingUser, user, loadInventory]);

  useEffect(() => {
    setConfirmDiscard(false);
    setConfirmXpReplace(false);
    setConfirmHealthPartial(false);
  }, [detailItem?.id]);

  const closeDetailModal = () => {
    setDetailItem(null);
    setConfirmDiscard(false);
    setConfirmXpReplace(false);
    setConfirmHealthPartial(false);
  };

  const hasActiveXpPotionBuff = (): boolean => {
    const b = inventory?.activeXpBoost;
    if (!b || b.bonusPercent <= 0) return false;
    if (typeof b.minutesRemaining === 'number' && Number.isFinite(b.minutesRemaining)) {
      return b.minutesRemaining > 0;
    }
    return parseUtcIsoMs(b.expiresAtUtc) > Date.now();
  };

  const tryUseItem = () => {
    if (!detailItem) return;
    if (isExperiencePotionItem(detailItem.itemTypeKey) && hasActiveXpPotionBuff()) {
      setConfirmDiscard(false);
      setConfirmXpReplace(true);
      return;
    }
    if (isHealthPotionItem(detailItem.itemTypeKey)) {
      const restore = healthPotionLifeRestoreCount(detailItem.itemTypeKey);
      if (shouldWarnHealthPotionOverflow(user, restore)) {
        setConfirmDiscard(false);
        setConfirmHealthPartial(true);
        return;
      }
    }
    void handleUseItem();
  };

  const handleUseItem = async () => {
    if (!detailItem) return;
    setUsing(true);
    try {
      await inventoryService.useItem(detailItem.id);
      toast.success(t('inventory.useSuccess', 'Item used.'));
      setConfirmXpReplace(false);
      setConfirmHealthPartial(false);
      closeDetailModal();
      await loadInventory();
      try {
        const u = await authService.getProfile();
        setUser(u);
      } catch {
        /* ignore */
      }
    } catch (e: unknown) {
      const msg =
        typeof e === 'object' && e !== null && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || t('inventory.useError', 'Could not use item.'));
    } finally {
      setUsing(false);
    }
  };

  const handleDiscardItem = async () => {
    if (!detailItem) return;
    setDiscarding(true);
    try {
      await inventoryService.discardItem(detailItem.id);
      toast.success(t('inventory.discardSuccess', 'Item discarded.'));
      closeDetailModal();
      await loadInventory();
    } catch (e: unknown) {
      const msg =
        typeof e === 'object' && e !== null && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || t('inventory.discardError', 'Could not discard the item.'));
    } finally {
      setDiscarding(false);
    }
  };

  const safeT = (key: string, defaultValue: string) => (ready ? t(key, defaultValue) : defaultValue);
  const busy = using || discarding;
  const detailPotionGrid = detailItem ? inventoryPotionGridMeta(detailItem.itemTypeKey) : null;

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    setUnreadNotificationCount(0);
    window.location.href = '/';
  };

  if (!ready || isLoadingUser) {
    return (
      <div className="profile-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{safeT('common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page inventory-page">
      <Navbar bg="white" expand="lg" className="home-navbar" collapseOnSelect>
        <Container fluid>
          <Navbar.Brand
            as={Link}
            to="/"
            className="navbar-brand d-flex align-items-center"
            aria-label="Quizter Club"
          >
            <NavbarBrandLogo />
            <span className="d-none d-lg-inline">Quizter Club</span>
          </Navbar.Brand>

          <div className="d-flex d-lg-none align-items-center ms-auto gap-1 gap-sm-2 navbar-mobile-toolbar">
            <PwaInstallButton className="flex-shrink-0" iconOnly />
            <LanguageSelector user={user} isInline={true} />
            <NavbarMusicToggle className="flex-shrink-0" />
            {user ? (
              <>
                <NotificationBell
                  className="d-lg-none"
                  unreadCount={unreadNotificationCount}
                  onUnreadCountChange={setUnreadNotificationCount}
                />
                <Navbar.Toggle aria-controls="inv-navbar" />
              </>
            ) : (
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0 home-navbar-login-icon"
                onClick={() => setShowLoginModal(true)}
                title={safeT('common.login', 'Login')}
                aria-label={safeT('common.login', 'Login')}
              >
                <i className="bi bi-box-arrow-in-right" aria-hidden />
              </Button>
            )}
          </div>
          <Navbar.Collapse id="inv-navbar" className="justify-content-lg-end">
            <Nav className="flex-column flex-lg-row align-items-stretch align-items-lg-center w-100 justify-content-lg-end">
              <div className="d-none d-lg-flex align-items-center gap-2 me-lg-3">
                <PwaInstallButton />
                <LanguageSelector user={user} isInline={true} />
                {!user && <NavbarMusicToggle />}
              </div>
              {user ? (
                <UserNavbarMenus
                  user={user}
                  pendingRequestsCount={pendingRequestsCount}
                  unreadNotificationCount={unreadNotificationCount}
                  onUnreadNotificationCountChange={setUnreadNotificationCount}
                  onOpenFriends={() => setShowFriendsList(true)}
                  onOpenPending={() => setShowPendingRequests(true)}
                  onLogout={handleLogout}
                />
              ) : (
                <Nav.Item className="d-none d-lg-block">
                  <Button variant="outline-primary" onClick={() => setShowLoginModal(true)}>
                    <i className="bi bi-box-arrow-in-right me-2" />
                    {safeT('common.login', 'Login')}
                  </Button>
                </Nav.Item>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container fluid className="home-content">
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8} xl={7}>
            {user ? (
              <Card className="profile-card">
                <Card.Body className="p-4">
                  <div className="inventory-page-header mb-3">
                    <h1 className="h3 mb-1">{safeT('inventory.title', 'Inventory')}</h1>
                    <p className="text-muted small mb-0">
                      {safeT('inventory.subtitle', 'Each item uses one slot. Items do not stack.')}
                    </p>
                  </div>

                  {inventory?.activeXpBoost && (
                    <Alert variant="info" className="mb-3 py-2">
                      {i18n.t('inventory.activeXpBoost', {
                        pct: inventory.activeXpBoost.bonusPercent,
                        until: new Date(parseUtcIsoMs(inventory.activeXpBoost.expiresAtUtc)).toLocaleString(
                          i18n.language || undefined,
                        ),
                      })}
                    </Alert>
                  )}

                  {loadError && (
                    <Alert variant="danger" className="mb-3">
                      {safeT('inventory.loadError', 'Could not load inventory.')}
                    </Alert>
                  )}

                  {listLoading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" />
                    </div>
                  ) : (
                    <div className="inventory-slots-wrap">
                      <Row className="g-3 justify-content-center mx-0">
                        {(inventory?.slots ?? []).map((slot) => {
                          const slotPotionGrid = slot.item ? inventoryPotionGridMeta(slot.item.itemTypeKey) : null;
                          const freeMaxSlots = inventory?.freeMaxSlots ?? 6;
                          const isVipExtraSlot = slot.slotIndex >= freeMaxSlots;
                          const isVipLocked = !!slot.isVipLocked;
                          const isVipActiveExtra = isVipExtraSlot && !isVipLocked;

                          if (isVipLocked) {
                            return (
                              <Col key={slot.slotIndex} xs={4} md={2} className="inventory-slot-col">
                                <button
                                  type="button"
                                  className="inventory-slot-square w-100 inventory-slot-square--locked"
                                  onClick={() => navigate('/vip')}
                                  aria-label={safeT('inventory.slotVipLocked', 'Unlock with VIP')}
                                  title={safeT('inventory.slotVipLocked', 'Unlock with VIP')}
                                >
                                  <div className="inventory-slot-inner d-flex flex-column align-items-center justify-content-center">
                                    <i className="bi bi-lock-fill inventory-slot-lock-icon" aria-hidden />
                                    <span className="inventory-slot-locked-label small text-center">
                                      {safeT('inventory.slotVipLocked', 'Unlock with VIP')}
                                    </span>
                                  </div>
                                </button>
                              </Col>
                            );
                          }

                          const vipBadgeTooltip = (
                            <Tooltip id={`inv-vip-slot-${slot.slotIndex}`}>
                              {safeT('inventory.vipSlotsBadgeTooltip', 'VIP benefit: Double slots')}
                            </Tooltip>
                          );

                          return (
                            <Col key={slot.slotIndex} xs={4} md={2} className="inventory-slot-col">
                              <div className="inventory-slot-wrapper">
                                <button
                                  type="button"
                                  className={`inventory-slot-square w-100 ${slot.item ? 'inventory-slot-square--filled' : 'inventory-slot-square--empty'}`}
                                  onClick={() => slot.item && setDetailItem(slot.item)}
                                  disabled={!slot.item}
                                  aria-label={
                                    slot.item
                                      ? safeT('inventory.openItem', 'Open item')
                                      : safeT('inventory.slotEmpty', 'Empty slot')
                                  }
                                >
                                  {slot.item ? (
                                    <div className="inventory-slot-inner d-flex flex-column align-items-center justify-content-center">
                                      {slotPotionGrid ? (
                                        <InventoryPotionSprite
                                          variant={slotPotionGrid.variant}
                                          size={slotPotionGrid.size}
                                          frameDisplayPx={52}
                                          className="inventory-slot-sprite mb-1"
                                        />
                                      ) : (
                                        <i className={`bi ${itemIconClass(slot.item.itemTypeKey)} inventory-slot-icon`} aria-hidden />
                                      )}
                                      <span className="inventory-slot-label small text-center">
                                        {ready
                                          ? i18n.t(`inventory.items.${slot.item.itemTypeKey}.short`, {
                                              defaultValue: slot.item.itemTypeKey,
                                            })
                                          : slot.item.itemTypeKey}
                                      </span>
                                      {slot.item.expiresAtUtc && (
                                        <span className="inventory-slot-expiry tiny text-muted">
                                          {new Date(slot.item.expiresAtUtc).toLocaleDateString(i18n.language || undefined)}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted small">{safeT('inventory.slotEmpty', 'Empty')}</span>
                                  )}
                                </button>
                                {isVipActiveExtra && (
                                  <OverlayTrigger placement="top" overlay={vipBadgeTooltip}>
                                    <PulseBadge variant="orange" size="xs" className="inventory-slot-vip-badge" tabIndex={0}>
                                      {safeT('inventory.vipSlotBadge', 'VIP')}
                                    </PulseBadge>
                                  </OverlayTrigger>
                                )}
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    </div>
                  )}

                  <div className="back-to-home-card d-flex gap-3 justify-content-center flex-wrap">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      type="button"
                      onClick={() => navigate('/')}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      {safeT('navigation.backToHome', 'Back to Home')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <Card className="profile-card">
                <Card.Body className="p-4 text-center">
                  <Alert variant="info" className="text-start mb-4">
                    {safeT('inventory.loginRequired', 'Log in to view your inventory.')}
                  </Alert>
                  <Button variant="primary" onClick={() => setShowLoginModal(true)}>
                    {safeT('common.login', 'Login')}
                  </Button>
                  <div className="back-to-home-card d-flex gap-3 justify-content-center flex-wrap">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      type="button"
                      onClick={() => navigate('/')}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      {safeT('navigation.backToHome', 'Back to Home')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>

      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{safeT('auth.loginRequired', 'Login Required')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoginPage
            onLoginSuccess={() => {
              setShowLoginModal(false);
              void loadUser();
            }}
          />
        </Modal.Body>
      </Modal>

      <Modal show={detailItem !== null} onHide={() => !busy && closeDetailModal()} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2 flex-wrap">
            {detailItem && confirmXpReplace ? (
              safeT('inventory.xpPotionReplaceTitle', 'Replace active XP boost?')
            ) : detailItem && confirmHealthPartial ? (
              safeT('inventory.healthPotionPartialTitle', 'Partial recovery?')
            ) : detailItem && confirmDiscard ? (
              safeT('inventory.discardConfirmTitle', 'Discard this item?')
            ) : detailItem ? (
              <>
                {detailPotionGrid ? (
                  <InventoryPotionSprite
                    variant={detailPotionGrid.variant}
                    size={detailPotionGrid.size}
                    frameDisplayPx={40}
                    className="inventory-modal-potion-sprite"
                  />
                ) : null}
                <span>
                  {i18n.t(`inventory.items.${detailItem.itemTypeKey}.name`, { defaultValue: detailItem.itemTypeKey })}
                </span>
              </>
            ) : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailItem && confirmXpReplace && inventory?.activeXpBoost ? (
            <p className="mb-0">
              {i18n.t('inventory.xpPotionReplaceBody', {
                count: xpBuffMinutesRemainingForDisplay(inventory.activeXpBoost),
                currentPct: inventory.activeXpBoost.bonusPercent,
                newPct: xpPotionBonusPercentForItem(detailItem.itemTypeKey) ?? 0,
              })}
            </p>
          ) : null}
          {detailItem && confirmHealthPartial && user ? (
            <p className="mb-0">
              {(() => {
                const current = user.livesStatus?.lives ?? user.lives ?? 0;
                const max = user.livesStatus?.maxLives ?? MAX_LIFE_UNITS_DEFAULT;
                const restore = healthPotionLifeRestoreCount(detailItem.itemTypeKey);
                const { effective, wasted } = healthPotionEffectiveRestore(current, max, restore);
                return buildHealthPotionPartialMessage(i18n.t.bind(i18n), current, max, restore, effective, wasted);
              })()}
            </p>
          ) : null}
          {detailItem && !confirmDiscard && !confirmXpReplace && !confirmHealthPartial && (
            <>
              {detailPotionGrid ? (
                <div className="d-flex justify-content-center mb-3">
                  <InventoryPotionSprite
                    variant={detailPotionGrid.variant}
                    size={detailPotionGrid.size}
                    frameDisplayPx={72}
                  />
                </div>
              ) : null}
              <p className="mb-2">
                {i18n.t(`inventory.items.${detailItem.itemTypeKey}.description`, { defaultValue: '' })}
              </p>
              {detailItem.expiresAtUtc && (
                <p className="small text-muted mb-0">
                  {i18n.t('inventory.expires', {
                    d: new Date(detailItem.expiresAtUtc).toLocaleString(i18n.language || undefined),
                  })}
                </p>
              )}
            </>
          )}
          {detailItem && confirmDiscard && (
            <p className="mb-0 text-danger-emphasis">{safeT('inventory.discardConfirmBody', 'This action is permanent.')}</p>
          )}
        </Modal.Body>
        <Modal.Footer className="flex-wrap gap-2">
          {detailItem && confirmXpReplace ? (
            <>
              <Button variant="secondary" onClick={() => setConfirmXpReplace(false)} disabled={busy}>
                {safeT('inventory.xpPotionReplaceBack', 'Back')}
              </Button>
              <Button variant="primary" onClick={() => void handleUseItem()} disabled={busy}>
                {using ? <Spinner size="sm" animation="border" /> : safeT('inventory.xpPotionReplaceContinue', 'Yes, continue')}
              </Button>
            </>
          ) : detailItem && confirmHealthPartial ? (
            <>
              <Button variant="secondary" onClick={() => setConfirmHealthPartial(false)} disabled={busy}>
                {safeT('inventory.healthPotionPartialCancel', 'Cancel')}
              </Button>
              <Button variant="primary" onClick={() => void handleUseItem()} disabled={busy}>
                {using ? <Spinner size="sm" animation="border" /> : safeT('inventory.healthPotionPartialConfirm', 'Use anyway')}
              </Button>
            </>
          ) : detailItem && confirmDiscard ? (
            <>
              <Button variant="secondary" onClick={() => setConfirmDiscard(false)} disabled={busy}>
                {safeT('common.back', 'Back')}
              </Button>
              <Button variant="danger" onClick={() => void handleDiscardItem()} disabled={busy}>
                {discarding ? <Spinner size="sm" animation="border" /> : safeT('inventory.discardConfirmAction', 'Yes, discard')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={closeDetailModal} disabled={busy}>
                {safeT('common.cancel', 'Cancel')}
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => {
                  setConfirmXpReplace(false);
                  setConfirmHealthPartial(false);
                  setConfirmDiscard(true);
                }}
                disabled={busy}
              >
                {safeT('inventory.discardItem', 'Discard')}
              </Button>
              <Button variant="primary" onClick={() => void tryUseItem()} disabled={busy}>
                {using ? <Spinner size="sm" animation="border" /> : safeT('inventory.useItem', 'Use item')}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {showFriendsList && <FriendsList onClose={() => setShowFriendsList(false)} />}
      {showPendingRequests && (
        <PendingRequests
          onClose={() => setShowPendingRequests(false)}
          onRequestResponded={() => {
            void friendshipService.getPendingRequests().then((r) => setPendingRequestsCount(r.length));
          }}
        />
      )}
    </div>
  );
};

export default InventoryPage;
